/**
 * The single source of truth for how a form control looks.
 *
 * Before this existed the repo carried 224 distinct class signatures across 502
 * controls. Everything that renders a box you can type in — BaseInput,
 * BaseTextarea, BaseSelect — builds its classes here, so there is exactly one
 * place to change the height, radius or focus treatment of a field.
 */

export type ControlSize = 'sm' | 'md' | 'lg'

/**
 * Height is set explicitly rather than left to padding + line-height.
 *
 * That was the bug: a control's height used to fall out of its font size and
 * vertical padding, so the same nominal size rendered 38px in one place and
 * 40px in another, and an affix field came out taller than the plain field
 * beside it. Fixing the height makes every control on a row line up no matter
 * what is inside it. Values match --fx-control-h* in base.css.
 */
export const CONTROL_HEIGHT: Record<ControlSize, string> = {
  sm: 'h-8', // 32px
  md: 'h-[38px]',
  lg: 'h-11', // 44px
}

/** Horizontal padding and type size. No vertical padding — the height centres it. */
export const CONTROL_SIZE: Record<ControlSize, string> = {
  sm: 'px-3 text-sm',
  md: 'px-3.5 text-sm',
  lg: 'px-4 text-base',
}

/** A textarea grows with its rows, so it keeps padding instead of a height. */
export const TEXTAREA_SIZE: Record<ControlSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3.5 py-2 text-sm',
  lg: 'px-4 py-3 text-base',
}

/**
 * rounded-md (6px) matches --fx-radius-control in base.css, which is also fed
 * to --el-border-radius-base, so our controls and Element Plus's agree.
 */
const CONTROL_BASE =
  'rounded-md border bg-white text-gray-900 transition-colors ' +
  'placeholder:text-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-offset-0 ' +
  'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed'

/** Teal owns focus — accent-600 is 3.16:1 on white, clearing SC 1.4.11. */
const CONTROL_VALID = 'border-gray-300 hover:border-gray-400 focus:border-accent-600 focus:ring-accent-600'

/** Error state stays red: status colour is semantic, not brand. */
const CONTROL_INVALID = 'border-red-500 focus:border-red-500 focus:ring-red-500'

export interface ControlOptions {
  /** A textarea grows with its rows instead of taking a fixed height. */
  multiline?: boolean
  /** The caller set its own width, so `w-full` must not be imposed. */
  ownWidth?: boolean
  /** The caller set its own height, so the fixed height must not be imposed. */
  ownHeight?: boolean
}

/**
 * Tailwind emits `w-full` and `h-[38px]` after the numbered and auto scales, so
 * a caller's `w-24` or `h-2` loses to them at equal specificity no matter what
 * order the class attribute is written in. Rather than make every call site
 * remember `!w-24`, the primitive simply does not impose a dimension the caller
 * has already set.
 */
export function controlClasses(
  size: ControlSize,
  invalid: boolean,
  { multiline = false, ownWidth = false, ownHeight = false }: ControlOptions = {},
): string {
  const parts = [CONTROL_BASE]
  if (!ownWidth) parts.push('w-full')

  if (multiline) parts.push(TEXTAREA_SIZE[size])
  else {
    if (!ownHeight) parts.push(CONTROL_HEIGHT[size])
    parts.push(CONTROL_SIZE[size])
  }

  parts.push(invalid ? CONTROL_INVALID : CONTROL_VALID)
  return parts.join(' ')
}

/**
 * Does a caller's class set this dimension? `max-w-*` and `min-w-*` do not
 * count — capping a full-width control is the normal, working combination.
 */
export function setsDimension(cls: unknown, prefix: 'w-' | 'h-'): boolean {
  if (typeof cls !== 'string') return false
  return cls.split(/\s+/).some((t) => t.startsWith(prefix))
}

/** Shared by BaseCheckbox and BaseRadio. Fill is navy so the mark stays legible. */
/**
 * `accent-primary-600` is what actually paints the box. The `text-primary-600`
 * trick only works with @tailwindcss/forms, which this project does not
 * install — without it a checkbox keeps the browser's default blue, which is
 * what shipped until now.
 */
export const CHOICE_CONTROL =
  'w-4 h-4 shrink-0 border-gray-300 accent-primary-600 cursor-pointer ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

/*
 * No focus ring here on purpose. A `ring` is a box-shadow fired on :focus, so
 * it appeared on mouse click as well as keyboard — a heavy offset box around a
 * 16px control every time you ticked it, and on a radio it read as a square.
 *
 * These fall through to the global :focus-visible outline in base.css instead:
 * keyboard focus only, teal, and an outline follows the element's own
 * border-radius, so it is round on a radio and square-ish on a checkbox by
 * itself. Text inputs keep their :focus ring — there you do want to see where
 * you are typing.
 */
